import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Award, Mail } from "lucide-react";
import SEO from "../components/SEO";
import "./BlogArticle.css";

export const SalaryVsDividendsUK2026 = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Salary vs Dividends: What's Best for Directors in 2026?",
    "description": "Complete guide to salary vs dividends for UK limited company directors. Tax-efficient income extraction strategies explained.",
    "author": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Brightson Accounting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://brightsonaccounting.co.uk/logo.png"
      }
    },
    "datePublished": "2026-04-10",
    "dateModified": "2026-04-10"
  };

  return (
    <div className="article-page">
      <SEO
        title="Salary vs Dividends: What's Best for Directors in 2026?"
        description="Complete guide to salary vs dividends for UK limited company directors. Tax-efficient income extraction strategies explained."
        canonical="/blog/salary-vs-dividends-uk-2026"
        keywords="salary vs dividends UK, director income, dividend tax UK, tax efficient income, limited company director pay"
        schema={JSON.stringify(articleSchema)}
      />

      <section className="article-header">
        <div className="container">
          <div className="article-back">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
          <div className="article-header-content">
            <span className="article-category-badge">Growing your company</span>
            <h1 className="article-main-title">Salary vs Dividends: What's Best for Directors in 2026?</h1>
            <p className="article-subtitle">Tax-Efficient Income Extraction from Brightson Accounting, Wolverhampton</p>
            <div className="article-meta-bar">
              <div className="meta-author">
                <Award size={18} />
                <span>Brightson Accounting (FCCA Qualified)</span>
              </div>
              <div className="meta-info">
                <Calendar size={16} />
                <span>10 April 2026</span>
                <Clock size={16} />
                <span>8 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="article-content">
        <div className="container">
          <div className="article-body">
            
            <p className="article-lead">
              As a limited company director, how you pay yourself makes a huge difference to your tax bill.
            </p>

            <p>
              The right mix of salary and dividends can save you thousands of pounds every year — completely legally.
            </p>

            <p>
              At <Link to="/services">Brightson Accounting in Wolverhampton</Link>, we help directors across the West Midlands optimize their income extraction while staying HMRC-compliant.
            </p>

            <div className="info-box">
              <strong>Quick Summary</strong>
              <ul>
                <li>Optimal salary: £12,570 (tax-free Personal Allowance)</li>
                <li>Take remaining profit as dividends (taxed at 8.75%)</li>
                <li>Dividends don't attract National Insurance</li>
                <li>£500 dividend allowance tax-free</li>
                <li>This saves £2,000-£3,000/year vs all-salary approach</li>
              </ul>
            </div>

            <h2>How Salary Works</h2>

            <p>
              Salary is subject to:
            </p>

            <ul>
              <li><strong>Income Tax:</strong> 0% up to £12,570, then 20%, 40%, 45%</li>
              <li><strong>Employer's NI:</strong> 13.8% on salary above £9,100</li>
              <li><strong>Employee's NI:</strong> 12% on salary between £12,570-£50,270</li>
            </ul>

            <p>
              Total tax on £50,000 salary: ~£14,000 (28%)
            </p>

            <h2>How Dividends Work</h2>

            <p>
              Dividends are paid from post-Corporation Tax profit.
            </p>

            <p>
              Tax rates (2026/27):
            </p>

            <ul>
              <li><strong>£0-£500:</strong> 0% (Dividend Allowance)</li>
              <li><strong>Basic rate:</strong> 8.75%</li>
              <li><strong>Higher rate:</strong> 33.75%</li>
              <li><strong>Additional rate:</strong> 39.35%</li>
            </ul>

            <p>
              Crucially: No National Insurance on dividends.
            </p>

            <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <p style={{ margin: 0 }}>
                <strong>💡 Want to see how much tax you could save?</strong><br/><br/>
                Most business owners we speak to are overpaying without realising it.<br/><br/>
                <Link to="/corporation-tax-calculator" style={{ color: '#4caf50', fontWeight: 600 }}>
                  👉 Try the Corporation Tax Calculator
                </Link>
              </p>
            </div>

            <h2>The Optimal Strategy (2026)</h2>

            <h3>Step 1: Pay Yourself a Small Salary</h3>

            <p>
              Most accountants recommend a salary of <strong>£12,570</strong> (the Personal Allowance).
            </p>

            <p>
              Why?
            </p>

            <ul>
              <li>No Income Tax</li>
              <li>No Employee's NI</li>
              <li>Minimal Employer's NI (only £478)</li>
              <li>Still builds National Insurance credits (for state pension)</li>
            </ul>

            <h3>Step 2: Take the Rest as Dividends</h3>

            <p>
              After salary and Corporation Tax, pay yourself dividends.
            </p>

            <p>
              Example: £50,000 profit
            </p>

            <ul>
              <li>Salary: £12,570</li>
              <li>Remaining profit: £37,430</li>
              <li>Corporation Tax (19%): £7,112</li>
              <li>Available for dividends: £30,318</li>
              <li>Dividend tax (8.75%): £2,609</li>
              <li><strong>Total tax: £9,721 (19.4%)</strong></li>
            </ul>

            <p>
              Compare to £50,000 all-salary: £14,000 tax (28%)
            </p>

            <p>
              <strong>Saving: £4,279/year</strong>
            </p>

            <p>
              From what we see with clients in Wolverhampton, this strategy is the most tax-efficient for most directors.
            </p>

            <h2>When to Take More Salary</h2>

            <p>
              In some situations, a higher salary makes sense:
            </p>

            <h3>1. Building Pension Contributions</h3>

            <p>
              If you want to contribute to a personal pension, you need "relevant UK earnings" (salary).
            </p>

            <h3>2. Applying for a Mortgage</h3>

            <p>
              Lenders often prefer consistent salary over dividends.
            </p>

            <h3>3. Claiming Tax Credits or Benefits</h3>

            <p>
              Some benefits are based on salary, not dividends.
            </p>

            <h2>The Dividend Allowance</h2>

            <p>
              The first £500 of dividends are tax-free (2026/27).
            </p>

            <p>
              This is in addition to your Personal Allowance.
            </p>

            <h2>Timing Dividends</h2>

            <p>
              You can declare dividends at any time during the year — as long as you have sufficient profit.
            </p>

            <p>
              Strategic timing:
            </p>

            <ul>
              <li>Spread dividends across tax years to use multiple allowances</li>
              <li>Delay dividends if you're expecting lower-rate years</li>
              <li>Take dividends before tax rate increases</li>
            </ul>

            <h2>Dividend Rules You Must Follow</h2>

            <p>
              Dividends can only be paid if:
            </p>

            <ul>
              <li>Your company has sufficient retained profit</li>
              <li>You follow proper board procedures (minutes, dividend vouchers)</li>
              <li>All shareholders receive dividends in proportion to their shares</li>
            </ul>

            <p className="highlight-note">
              Never pay dividends when the company is loss-making — it's illegal and can pierce your limited liability.
            </p>

            <h2>What About Multiple Directors?</h2>

            <p>
              If you have multiple shareholders/directors, dividends must be paid proportionally.
            </p>

            <p>
              Example:
            </p>

            <ul>
              <li>Director A: 60% shares → 60% of dividends</li>
              <li>Director B: 40% shares → 40% of dividends</li>
            </ul>

            <p>
              You cannot pay different amounts to different shareholders (unless you have different share classes).
            </p>

            <h2>Record-Keeping Requirements</h2>

            <p>
              For every dividend payment, you must:
            </p>

            <ul>
              <li>Hold a board meeting (or written resolution)</li>
              <li>Record the decision in board minutes</li>
              <li>Issue dividend vouchers to each shareholder</li>
              <li>Keep records for at least 6 years</li>
            </ul>

            <p>
              Most <Link to="/services">accountants in Wolverhampton</Link> can handle this as part of year-end compliance.
            </p>

            <h2>Common Mistakes</h2>

            <p>
              We see these errors with directors in Birmingham and Wolverhampton:
            </p>

            <ul>
              <li>Taking all profit as salary (overpaying tax)</li>
              <li>Paying dividends without board minutes</li>
              <li>Declaring dividends when the company is loss-making</li>
              <li>Not issuing dividend vouchers</li>
              <li>Paying different amounts to different shareholders</li>
            </ul>

            <h2>Tax Planning Across the Year</h2>

            <p>
              Don't wait until year-end to decide on salary vs dividends.
            </p>

            <p>
              Plan quarterly:
            </p>

            <ul>
              <li>Review profit forecast</li>
              <li>Estimate Corporation Tax liability</li>
              <li>Plan dividend payments</li>
              <li>Adjust if profit changes</li>
            </ul>

            <p>
              Read: <Link to="/blog/tax-planning-growing-business-uk">Tax Planning Strategies for Growing Businesses</Link>
            </p>

            <h2>When to Get Professional Advice</h2>

            <p>
              Salary vs dividends strategy gets more complex when:
            </p>

            <ul>
              <li>You have multiple income sources</li>
              <li>You're close to higher tax thresholds</li>
              <li>You have multiple shareholders</li>
              <li>You're planning for retirement</li>
            </ul>

            <p>
              An accountant can model different scenarios and find the optimal strategy for your situation.
            </p>

            <div className="article-cta-box">
              <h2>Ready to Reduce Your Tax Bill?</h2>
              <p>
                We help businesses across Wolverhampton and the West Midlands reduce tax legally, improve cash flow, and stay compliant.
              </p>
              <Link to="/contact" className="article-cta-button">
              <Mail size={20} />
                Book a Free Consultation
              </Link>
            </div>

            <div className="disclaimer">
              <h3>Disclaimer</h3>
              <p>
                This content is for general guidance only. For tailored advice, contact Brightson Accounting.
              </p>
            </div>

          </div>

          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-heading">Need Help?</h3>
              <p>Speak to our FCCA-qualified accountants about income extraction.</p>
              <Link to="/contact" className="sidebar-button">
                <Mail size={18} />
                Contact Us
              </Link>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Related Articles</h3>
              <div className="related-links">
                <Link to="/blog/tax-planning-growing-business-uk" className="related-link">Tax Planning Strategies</Link>
                <Link to="/blog/when-to-hire-accountant-uk" className="related-link">When to Hire Accountant</Link>
                <Link to="/blog/scale-business-uk" className="related-link">Scale Your Business</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default SalaryVsDividendsUK2026;
